'use client';

import { Link } from 'react-router-dom';
import { V2Layout, RevealSection, useV2Lang, Eyebrow, Rule, ACCENT } from '../../_layout';

const COPY = {
  CN: {
    kicker: '长期出版 · 编纂中',
    titleA: '《中国乐队',
    titleB: '欧洲巡演手册》',
    soon: '敬请期待',
    soonSub: 'Coming soon',
    lede: '一本面向中国独立乐队的实操手册——从签证到合同、从运输到欧洲场地与节展生态。我们在认真编纂，不愿意拼凑成一本过期就废的便利贴。',
    update: '主要章节素材已搜集，正在分章节细化与法律审校。预计 2026 年下半年发布预览版。所有内容为公益出版，发布后免费下载。',
    cta: { kicker: '想第一时间拿到？', body: '留下邮箱，发布预览版时第一时间发给你；中国乐队、booking agent、欧洲合作方都欢迎登记。', email: 'gravilens@pathin.eu', subject: '订阅：巡演手册预览版', cta: '写邮件登记 →' },
    back: '← 返回 GraviLens',
  },
  EN: {
    kicker: 'Long-form publishing · in preparation',
    titleA: 'China-band',
    titleB: 'Europe Tour Handbook',
    soon: 'Coming soon',
    soonSub: '敬请期待',
    lede: "A practical handbook for Chinese independent bands — visas, contracts, logistics, the European venue and festival ecosystem. We are taking our time. We don't want to publish a stack of convenience that's stale by the time it lands.",
    update: 'Source material is gathered. Currently refining chapter detail and running legal review. Preview release expected late 2026. Public-good publication: free download.',
    cta: { kicker: 'Want it first?', body: 'Leave your email and we will send the preview the moment it lands. Chinese bands, booking agents, European partners — all welcome to sign up.', email: 'gravilens@pathin.eu', subject: 'Subscribe: Tour Handbook preview', cta: 'Email to sign up →' },
    back: '← Back to GraviLens',
  },
  DE: {
    kicker: 'Langform-Publishing · in Vorbereitung',
    titleA: 'China-Band',
    titleB: 'Europatournee-Handbuch',
    soon: 'Demnächst',
    soonSub: '敬请期待',
    lede: 'Ein praktisches Handbuch für chinesische Independent-Bands — von Visa über Verträge bis Logistik und das europäische Venue- und Festival-Ökosystem. Wir nehmen uns Zeit. Wir wollen kein hastiges Heft veröffentlichen, das beim Erscheinen schon überholt ist.',
    update: 'Quellenmaterial ist gesammelt. Wir verfeinern derzeit die Kapitel und führen die juristische Prüfung durch. Preview-Release Ende 2026 erwartet. Gemeinwohl-Publikation: kostenlos zum Download.',
    cta: { kicker: 'Als Erste:r erhalten?', body: 'Hinterlassen Sie Ihre E-Mail — wir schicken die Vorschau, sobald sie da ist. Chinesische Bands, Booking-Agenturen, europäische Partner: alle willkommen.', email: 'gravilens@pathin.eu', subject: 'Anmeldung: Tour-Handbuch Vorschau', cta: 'Per E-Mail anmelden →' },
    back: '← Zurück zu GraviLens',
  },
};

export default function V2Handbook() {
  const lang = useV2Lang();
  const t = COPY[lang];
  return (
    <V2Layout hideFooterContact>
      {/* Coming-soon hero — single, calm view. No table-of-contents. */}
      <RevealSection className="relative min-h-[88vh] flex items-center px-6 md:px-10 pt-32 pb-24">
        <div className="mx-auto max-w-[1400px] w-full">
          <Link
            to="/GraviLens"
            className="inline-block text-[12px] font-mono uppercase tracking-[0.18em] text-white/65 hover:text-white mb-12 font-medium"
          >
            {t.back}
          </Link>
          <Eyebrow accent>{t.kicker}</Eyebrow>
          <h1 className="v2-cjk-tight mt-8 text-[clamp(56px,11vw,200px)] leading-[0.9] tracking-[-0.05em] font-black">
            <span className="block text-white">{t.titleA}</span>
            <span className="block text-white">{t.titleB}</span>
          </h1>

          <div className="mt-14 grid gap-12 md:grid-cols-12 items-start">
            <div className="md:col-span-6">
              <p className="text-[clamp(17px,1.45vw,21px)] leading-[1.7] text-white/80 max-w-prose">
                {t.lede}
              </p>
              <p className="mt-8 text-[15px] leading-[1.7] text-white/75 max-w-[58ch]">
                {t.update}
              </p>
            </div>
            <div className="md:col-span-3 md:col-start-10">
              <div className="border-t pt-4" style={{ borderColor: ACCENT }}>
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>
                  {t.soon}
                </p>
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/55 mt-1 font-medium">
                  {t.soonSub}
                </p>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* CTA */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow accent>{t.cta.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <p className="v2-cjk-tight text-[clamp(20px,2vw,28px)] leading-[1.45] text-white/85 max-w-[55ch]">
              {t.cta.body}
            </p>
            <a
              href={`mailto:${t.cta.email}?subject=${encodeURIComponent(t.cta.subject)}`}
              className="inline-block mt-8 px-6 py-3 border text-[14px] tracking-wide hover:bg-white hover:text-black transition-colors"
              style={{ borderColor: 'rgba(255,255,255,0.4)' }}
            >
              {t.cta.cta}
            </a>
          </div>
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}
